import { useState, useEffect } from "react";
import axios from "axios";
import { Button } from "./ui/button";
import { toast } from "sonner";
import { Bell, BellOff, Download, Smartphone } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
} from "./ui/dialog";

const API = `${window.location.origin}/api`;

// Convert base64 to Uint8Array for VAPID key
function urlBase64ToUint8Array(base64String) {
  const padding = '='.repeat((4 - base64String.length % 4) % 4);
  const base64 = (base64String + padding)
    .replace(/-/g, '+')
    .replace(/_/g, '/');
  const rawData = window.atob(base64);
  const outputArray = new Uint8Array(rawData.length);
  for (let i = 0; i < rawData.length; ++i) {
    outputArray[i] = rawData.charCodeAt(i);
  }
  return outputArray;
}

const PushNotificationManager = ({ onAction }) => {
  const [isSupported, setIsSupported] = useState(false);
  const [isSubscribed, setIsSubscribed] = useState(false);
  const [isPWAInstallable, setIsPWAInstallable] = useState(false);
  const [isPWAInstalled, setIsPWAInstalled] = useState(false);
  const [deferredPrompt, setDeferredPrompt] = useState(null);
  const [showInstallDialog, setShowInstallDialog] = useState(false);
  const [isIOS, setIsIOS] = useState(false);

  useEffect(() => {
    // Check if push is supported
    const supported = 'serviceWorker' in navigator && 'PushManager' in window;
    setIsSupported(supported);

    // Check if iOS
    const ios = /iPad|iPhone|iPod/.test(navigator.userAgent) && !window.MSStream;
    setIsIOS(ios);

    // Check if already installed as PWA
    const isStandalone = window.matchMedia('(display-mode: standalone)').matches || 
                         window.navigator.standalone === true;
    setIsPWAInstalled(isStandalone);

    // Check subscription status
    if (supported) {
      checkSubscription();
    }

    // Listen for beforeinstallprompt event (Chrome/Edge)
    const handleBeforeInstall = (e) => {
      e.preventDefault();
      setDeferredPrompt(e);
      setIsPWAInstallable(true);
    };

    window.addEventListener('beforeinstallprompt', handleBeforeInstall);

    // Listen for app installed
    window.addEventListener('appinstalled', () => {
      setIsPWAInstalled(true);
      setIsPWAInstallable(false);
      toast.success("ODR Academy installata!");
    });

    return () => {
      window.removeEventListener('beforeinstallprompt', handleBeforeInstall);
    };
  }, []);

  const checkSubscription = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      setIsSubscribed(!!subscription);
    } catch (error) {
      console.error('Error checking subscription:', error);
    }
  };

  const subscribeToNotifications = async () => {
    try {
      // Get VAPID public key from server
      const { data } = await axios.get(`${API}/push/vapid-key`);
      const vapidPublicKey = data.publicKey;

      if (!vapidPublicKey) {
        throw new Error('VAPID key not configured');
      }

      const registration = await navigator.serviceWorker.ready;
      
      // Request notification permission
      const permission = await Notification.requestPermission();
      if (permission !== 'granted') {
        toast.error("Permesso notifiche negato. Attivalo dalle impostazioni.");
        return;
      }

      // Subscribe to push
      const subscription = await registration.pushManager.subscribe({
        userVisibleOnly: true,
        applicationServerKey: urlBase64ToUint8Array(vapidPublicKey)
      });

      // Send subscription to server
      await axios.post(`${API}/push/subscribe`, {
        subscription: {
          endpoint: subscription.endpoint,
          keys: {
            p256dh: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('p256dh')))),
            auth: btoa(String.fromCharCode(...new Uint8Array(subscription.getKey('auth'))))
          }
        }
      });

      setIsSubscribed(true);
      toast.success("Notifiche attivate! 🔔");
    } catch (error) {
      console.error('Subscription error:', error);
      toast.error("Errore nell'attivazione delle notifiche");
    }
  };

  const unsubscribeFromNotifications = async () => {
    try {
      const registration = await navigator.serviceWorker.ready;
      const subscription = await registration.pushManager.getSubscription();
      
      if (subscription) {
        await subscription.unsubscribe();
        await axios.delete(`${API}/push/unsubscribe`);
      }
      
      setIsSubscribed(false);
      toast.success("Notifiche disattivate");
    } catch (error) {
      console.error('Unsubscribe error:', error);
      toast.error("Errore nella disattivazione");
    }
  };

  const installPWA = async () => {
    // Close mobile menu first
    if (onAction) onAction();
    
    if (isIOS) {
      setShowInstallDialog(true);
      return;
    }

    if (deferredPrompt) {
      deferredPrompt.prompt();
      const { outcome } = await deferredPrompt.userChoice;
      if (outcome === 'accepted') {
        toast.success("Installazione in corso...");
      }
      setDeferredPrompt(null);
      setIsPWAInstallable(false);
    }
  };

  // Don't show anything if not supported
  if (!isSupported && !isPWAInstallable && !isIOS) {
    return null;
  }

  return (
    <>
      <div className="flex items-center gap-2">
        {/* Install PWA Button */}
        {!isPWAInstalled && (isPWAInstallable || isIOS) && (
          <Button
            variant="outline"
            size="sm"
            className="rounded-full"
            onClick={installPWA}
            data-testid="install-pwa-btn"
          >
            <Download className="w-4 h-4 mr-2" />
            Installa App
          </Button>
        )}

        {/* Notification Toggle Button */}
        {isSupported && (
          <Button
            variant={isSubscribed ? "default" : "outline"}
            size="sm"
            className="rounded-full"
            onClick={isSubscribed ? unsubscribeFromNotifications : subscribeToNotifications}
            data-testid="notification-toggle-btn"
          >
            {isSubscribed ? (
              <>
                <Bell className="w-4 h-4 mr-2" />
                Notifiche ON
              </>
            ) : (
              <>
                <BellOff className="w-4 h-4 mr-2" />
                Attiva Notifiche
              </>
            )}
          </Button>
        )}
      </div>

      {/* iOS Install Instructions Dialog */}
      <Dialog open={showInstallDialog} onOpenChange={setShowInstallDialog}>
        <DialogContent className="rounded-2xl">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-2">
              <Smartphone className="w-5 h-5" />
              Installa ODR Academy su iPhone/iPad
            </DialogTitle>
            <DialogDescription>
              Segui questi passaggi per installare l'app:
            </DialogDescription>
          </DialogHeader>
          <div className="space-y-4 py-4">
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-primary">1</span>
              </div>
              <p className="text-sm">
                Tocca l'icona <strong>Condividi</strong> (il quadrato con la freccia verso l'alto) nella barra di Safari
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-primary">2</span>
              </div>
              <p className="text-sm">
                Scorri e tocca <strong>"Aggiungi alla schermata Home"</strong>
              </p>
            </div>
            <div className="flex items-start gap-3">
              <div className="w-8 h-8 rounded-full bg-primary/10 flex items-center justify-center flex-shrink-0">
                <span className="font-bold text-primary">3</span>
              </div>
              <p className="text-sm">
                Tocca <strong>"Aggiungi"</strong> in alto a destra
              </p>
            </div>
            <div className="p-3 bg-amber-50 rounded-xl text-sm text-amber-800">
              <strong>Nota:</strong> Le notifiche push su iOS funzionano solo dopo aver installato l'app sulla schermata Home (iOS 16.4+)
            </div>
          </div>
          <Button className="w-full rounded-full" onClick={() => setShowInstallDialog(false)}>
            Ho capito
          </Button>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default PushNotificationManager;
